import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Search, Calendar, Eye, ArrowRight, BookOpen } from 'lucide-react';
import axios from 'axios';
import PaginationBar from '../components/PaginationBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;

const CATEGORY_COLORS = {
  'general': 'bg-gray-100 text-gray-700',
  'career-advice': 'bg-blue-100 text-blue-700',
  'industry-news': 'bg-purple-100 text-purple-700',
  'employer-tips': 'bg-emerald-100 text-emerald-700',
  'student-guide': 'bg-amber-100 text-amber-700',
  'recruitment': 'bg-red-100 text-red-700',
};


export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const activeCategory = searchParams.get('category') || 'all';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => { fetchPosts(); }, [activeCategory, page]);
  useEffect(() => { fetchCategories(); }, [activeCategory]);

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p > 1) params.set('page', String(p)); else params.delete('page');
    setSearchParams(params);
  };

  // A stale ?page=N beyond the last page falls back to the first page.
  useEffect(() => {
    if (!loading && page > 1 && posts.length === 0) goToPage(1);
  }, [loading, posts.length, page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // The API returns no total, so ask for one extra row to learn whether a next page exists.
      const res = await axios.get(`${API_URL}/api/blog`, {
        params: { ...(activeCategory !== 'all' ? { category: activeCategory } : {}), skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE + 1 },
      });
      const rows = res.data.posts || [];
      setHasMore(rows.length > PAGE_SIZE);
      setPosts(rows.slice(0, PAGE_SIZE));
    } catch { setPosts([]); setHasMore(false); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/blog/categories`);
      setCategories(res.data);
    } catch { setCategories([]); }
  };

  const filteredPosts = posts.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-4">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" />Career Blog
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Career Insights & News</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">Expert advice, industry news, and tips to help you succeed in your career</p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
            <Input
              className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20"
              placeholder="Search articles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-56 shrink-0 hidden md:block">
            <div className="sticky top-24 space-y-4">
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Categories</h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSearchParams({})}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeCategory === 'all' ? 'bg-[#003366] text-white' : 'hover:bg-gray-100'}`}
                    >
                      All Posts
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.name}
                        onClick={() => setSearchParams({ category: cat.name })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${activeCategory === cat.name ? 'bg-[#003366] text-white' : 'hover:bg-gray-100'}`}
                      >
                        <span className="capitalize">{cat.name?.replace(/-/g, ' ')}</span>
                        <span className="text-xs opacity-60">{cat.post_count}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Posts */}
          <div className="flex-1">
            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}
              </div>
            ) : filteredPosts.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">No articles found</CardContent></Card>
            ) : (
              <div className="space-y-5">
                {filteredPosts.map((post, i) => (
                  <Link key={post.id} to={`/blog/${post.slug}`}>
                    <Card className="hover:shadow-xl transition-all cursor-pointer overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={CATEGORY_COLORS[post.category] || 'bg-gray-100 text-gray-700'}>
                                {post.category?.replace(/-/g, ' ')}
                              </Badge>
                              {i === 0 && page === 1 && activeCategory === 'all' && <Badge className="bg-[#E11D48] text-white">Featured</Badge>}
                            </div>
                            <h2 className="text-lg font-bold mb-2 hover:text-[#003366] transition-colors">{post.title}</h2>
                            <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{post.excerpt}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{(post.views || 0).toLocaleString()} views</span>
                              <span>By {post.author_name}</span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
                <PaginationBar page={page} pageSize={PAGE_SIZE} hasMore={hasMore} onPageChange={goToPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
